import styles from "./testCaseTemplateItem.module.scss";
import { TestCaseTemplate } from "../../../types/testCaseTemplateTypes";
import editIcon from "../../../shared/images/edit.svg";
import copyIcon from "../../../shared/images/copy.svg";
import deleteIcon from "../../../shared/images/delete.svg";
import { ReactComponent as MenuIcon } from "../../../shared/images/menu.svg";
import {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Popup } from "../../../shared/components/popup/popup";
import { useTranslation } from "react-i18next";
import arrowIcon from "../../../shared/images/arrow.svg";
import infoIcon from "../../../shared/images/info.svg";
import templatePositiveIcon from "../../../shared/images/templatePositive.svg";
import templateNegativeIcon from "../../../shared/images/templateNegative.svg";
import { ReactComponent as AddIcon } from "../../../shared/images/add.svg";
import { MessageWindow } from "../../../shared/components/messageWindow/messageWindow";
import { deleteSelectedTestCaseTemplate } from "../../../store/slices/testCaseTemplateSlice";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../../store";
import { useDeleteTestCaseTemplateMutation } from "../../../api/testCaseTemplateService";
import { setUpdateModal } from "../../../store/slices/mainSlice";

type TestCaseTemplateMenuItemProps = {
  testCaseTemplate: TestCaseTemplate;
  onClick: () => void;
  refetch: () => void;
  arrowClass: string;
  onEditClick: () => void;
  onCopyClick: () => void;
  onAddTestCaseClick: () => void;
}

export const TestCaseTemplateItem = ({ testCaseTemplate, onClick, refetch, onEditClick, onCopyClick, onAddTestCaseClick, arrowClass }: TestCaseTemplateMenuItemProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const selectedTestCaseTemplate = useSelector((state: RootState) => state.testCaseTemplateSlice.selectedTestCaseTemplate);
  const selectedTestCase = useSelector((state: RootState) => state.testCaseTemplateSlice.selectedTestCase);

  const menuContainer = useRef<HTMLDivElement>(null);

  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);

  const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>(false)

  const [deleteTestCaseTemplateMutation, { isSuccess: isSuccessDeleteTestCaseTemplate }] = useDeleteTestCaseTemplateMutation();

  useEffect(() => {
    if (isSuccessDeleteTestCaseTemplate) {
      refetch();
      dispatch(setUpdateModal({ show: true, isDelete: true }))
    }
  }, [isSuccessDeleteTestCaseTemplate]);

  const onClickMenuIcon = (): void => {
    setShowPopup(!showPopup);
  }

  const closeMenu = (): void => {
    setShowPopup(false);
  }

  const editClick = (): void => {
    setShowPopup(false);
    onEditClick();
  }

  const addTestCaseClick = (): void => {
    setShowPopup(false);
    onAddTestCaseClick();
  }

  const copyClick = (): void => {
    setShowPopup(false);
    onCopyClick();
  }

  const deleteClick = (): void => {
    setShowPopup(false);
    setShowDeleteMessage(true);
  }

  const closeMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm) {
      dispatch(deleteSelectedTestCaseTemplate());
      deleteTestCaseTemplateMutation(testCaseTemplate.id as number);
    }
    setShowDeleteMessage(false);
  }

  const isActive: boolean = useMemo(() => {
    return selectedTestCaseTemplate.id === testCaseTemplate.id && !selectedTestCase.id
  }, [selectedTestCaseTemplate, selectedTestCase]);

  return (
    <>
      <div className={`${styles.container} ${showMenu || isActive ? styles.hover : ""}`}
           onClick={onClick}
           onMouseEnter={() => setShowMenu(true)}
           onMouseLeave={() => setShowMenu(false)}>
        <div className={styles.info}>
          <div className={styles.arrowContainer}>
            {!(testCaseTemplate.childTestCases?.length === 0) &&
              (<img src={arrowIcon} alt=""
                    className={arrowClass}/>)
            }
          </div>

          <img src={testCaseTemplate.isPositive ? templatePositiveIcon : templateNegativeIcon} alt=""/>
          <div className={styles.name}>
            <span title={testCaseTemplate.name}>{testCaseTemplate.name}</span>
          </div>
        </div>
      </div>
      { (showMenu || showPopup) && (
        <div className={styles.actions}
             onMouseEnter={() => setShowMenu(true)}
             onMouseLeave={() => setShowMenu(false)}>
          {!!testCaseTemplate.description && (
            <img title={testCaseTemplate.description} src={infoIcon} alt=""/>
          )}
          <div className={styles.menu} ref={menuContainer}>
            <div className={styles.menuIcon}
                 onClick={onClickMenuIcon}>
              <MenuIcon/>
            </div>
            <Popup isShow={showPopup}
                   classContainer={styles.popup}
                   left={-70}
                   top={5}
                   parent={menuContainer.current}
                   onClose={closeMenu}
                   child={
                     <>
                       <div className={styles.item}
                            onClick={addTestCaseClick}>
                         <AddIcon/>
                         {t("common.addTestCase")}
                       </div>
                       <div className={styles.item}
                            onClick={copyClick}>
                         <img src={copyIcon} alt=""/>
                         {t("common.copy")}
                       </div>
                       <div className={styles.item}
                            onClick={editClick}>
                         <img src={editIcon} alt=""/>
                         {t("common.edit")}
                       </div>
                       <div className={styles.item}
                            onClick={deleteClick}>
                         <img src={deleteIcon} alt=""/>
                         {t("common.delete")}
                       </div>
                     </>
                   }
            />
          </div>
        </div>
      )}
      {showDeleteMessage && (
        <MessageWindow onClose={closeMessageWindow}
                       title={t("testCaseTemplate.delete")}
                       description={t("testCaseTemplate.deleteDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
    </>

  );
}